import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilmCard from "@/components/feature/FilmCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { EmptyState, LoadingState } from "@/components/base/StateViews";
import { getPopular, searchFilms } from "@/lib/filmData";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useSimulatedLoading } from "@/hooks/useSimulatedLoading";
import type { FilmType } from "@/types/film";

type TypeFilter = "all" | FilmType;

const TYPE_TABS: { label: string; value: TypeFilter }[] = [
  { label: "全部", value: "all" },
  { label: "电影", value: "movies" },
  { label: "电视剧", value: "tv" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  const [input, setInput] = useState(query);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

  useEffect(() => {
    setInput(query);
    setTypeFilter("all");
  }, [query]);

  const matched = useMemo(() => searchFilms(query), [query]);
  const results = useMemo(
    () => (typeFilter === "all" ? matched : matched.filter((film) => film.type === typeFilter)),
    [matched, typeFilter],
  );

  const loading = useSimulatedLoading([query, typeFilter], 400);
  const hot = getPopular(4);

  useDocumentMeta({
    title: query ? `搜索「${query}」 - 新金瓶梅` : "搜索影视 - 新金瓶梅",
    description: query
      ? `新金瓶梅搜索结果：与「${query}」相关的影视作品资讯与剧情介绍。`
      : "搜索新金瓶梅收录的电影与电视剧作品，快速找到你想看的剧情介绍。",
    keywords: `新金瓶梅,影视搜索,${query || "剧情介绍"},影视资源`,
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = input.trim();
    if (next) {
      setSearchParams({ q: next });
    } else {
      setSearchParams({});
    }
  };

  const countFor = (value: TypeFilter) =>
    value === "all" ? matched.length : matched.filter((film) => film.type === value).length;

  return (
    <div className="page-shell pb-8 pt-24 md:pt-28">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索影视" }]} />

      <header className="mt-2">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
          搜索影视
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
          输入影视名称、主演、地区或类型关键词，即可查询相关的作品资讯与剧情介绍。
        </p>
      </header>

      <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <i
            className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-foreground-400"
            aria-hidden="true"
          ></i>
          <input
            type="search"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="搜索电影、电视剧、主演或类型…"
            aria-label="搜索影视关键词"
            className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-search-line" aria-hidden="true"></i>
          搜索
        </button>
      </form>

      {query && (
        <div className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
          <span className="text-sm text-foreground-600">
            共找到 <span className="font-bold text-foreground-950">{results.length}</span> 条与
          </span>
          <mark className="rounded-md bg-accent-500 px-2.5 py-1 text-sm font-bold text-primary-950">
            {query}
          </mark>
          <span className="text-sm text-foreground-600">相关的影视内容</span>
        </div>
      )}

      {query && (
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {TYPE_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setTypeFilter(tab.value)}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                typeFilter === tab.value
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-600"
              }`}
            >
              {tab.label}
              <span
                className={`rounded-full px-1.5 text-[11px] ${
                  typeFilter === tab.value ? "bg-background-50/25" : "bg-background-200 text-foreground-500"
                }`}
              >
                {countFor(tab.value)}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="mt-6">
        {!query ? (
          <EmptyState
            icon="ri-search-eye-line"
            title="输入关键词开始搜索"
            description="试试搜索「古装」「悬疑」「武侠」等类型，或直接输入影视名称与主演姓名。"
          />
        ) : loading ? (
          <LoadingState label="正在搜索影视内容…" />
        ) : results.length === 0 ? (
          <EmptyState
            icon="ri-file-search-line"
            title="未找到匹配的影视内容"
            description={`没有找到与「${query}」相关的作品，换个关键词试试，或浏览下面的热门影视。`}
          />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {results.map((film) => (
              <FilmCard key={film.id} film={film} highlight={query} />
            ))}
          </div>
        )}
      </div>

      {(!query || (!loading && results.length === 0)) && (
        <section className="mt-12">
          <SectionHeading title="热门影视推荐" subtitle="大家最近都在看这些作品" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {hot.map((film) => (
              <FilmCard key={film.id} film={film} />
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/category/movies"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              <i className="ri-movie-2-line" aria-hidden="true"></i>
              浏览电影频道
            </Link>
            <Link
              to="/category/tv"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              <i className="ri-tv-2-line" aria-hidden="true"></i>
              浏览电视剧频道
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}